/**
 * <athlete-gallery>
 *
 * Drives the --ag-bias custom property on the element based on the cursor's
 * horizontal position across the entire viewport (not just the gallery):
 *   bias = 0   -> cursor at the right edge of the viewport  (right-state)
 *   bias = 1   -> cursor at the left edge of the viewport   (left-state)
 *   bias = 0.5 -> cursor exactly at viewport center          (midpoint)
 *
 * Falls back to 0 (right-state) when the viewport is below 990px or when the
 * user prefers reduced motion. The last known bias is preserved when the
 * cursor leaves the document.
 */
class AthleteGallery extends HTMLElement {
  static MOBILE_BREAKPOINT = 990;

  constructor() {
    super();
    this.grid = null;
    this.rafId = null;
    this.lastBias = 0;
    // Assume visible until the IntersectionObserver reports otherwise — first
    // callback fires asynchronously after observe().
    this.isVisible = true;
    this.handleMove = this.handleMove.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleMotionChange = this.handleMotionChange.bind(this);
    this.handleIntersect = this.handleIntersect.bind(this);
    this.handlePlay = this.handlePlay.bind(this);
    this.handleStop = this.handleStop.bind(this);
    this.handleEmbedClick = this.handleEmbedClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.grid = this.querySelector('.athlete-gallery__grid');
    if (!this.grid) return;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia(
      `(max-width: ${AthleteGallery.MOBILE_BREAKPOINT - 1}px)`
    );

    this.videoTrigger = this.querySelector('.athlete-gallery__video-trigger');
    this.embed = this.querySelector('.athlete-gallery__embed');
    this.iframe = this.querySelector('.athlete-gallery__embed-iframe');
    this.closeBtn = this.querySelector('.athlete-gallery__embed-close');
    this.videoId = this.dataset.videoId;
    this.videoType = this.dataset.videoType;

    // Skip the cursor-tracking work entirely when the section is offscreen.
    // Use a small rootMargin so we start updating slightly before the section
    // scrolls into view, avoiding a visible "snap" on first appearance.
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.handleIntersect, {
        rootMargin: '200px 0px',
      });
      this.intersectionObserver.observe(this);
    }

    this.bindEvents();
    this.updateActiveState();
  }

  disconnectedCallback() {
    this.unbindEvents();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    this.stop();
  }

  bindEvents() {
    // Track cursor across the entire viewport so the gallery keeps reacting
    // even when the cursor is outside the section's bounds.
    window.addEventListener('pointermove', this.handleMove, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.motionQuery.addEventListener('change', this.handleMotionChange);

    if (this.videoTrigger && this.embed && this.iframe) {
      this.videoTrigger.addEventListener('click', this.handlePlay);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.handleStop);
      this.embed.addEventListener('click', this.handleEmbedClick);
      document.addEventListener('keydown', this.handleKeyDown);
    }
  }

  unbindEvents() {
    window.removeEventListener('pointermove', this.handleMove);
    window.removeEventListener('resize', this.handleResize);
    this.motionQuery.removeEventListener('change', this.handleMotionChange);

    if (this.videoTrigger) this.videoTrigger.removeEventListener('click', this.handlePlay);
    if (this.closeBtn) this.closeBtn.removeEventListener('click', this.handleStop);
    if (this.embed) this.embed.removeEventListener('click', this.handleEmbedClick);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  updateActiveState() {
    const disabled = this.mobileQuery.matches || this.motionQuery.matches;
    this.classList.toggle('is-interactive', !disabled);
    if (disabled) this.setBias(0);
  }

  handleResize() {
    this.updateActiveState();
  }

  handleMotionChange() {
    this.updateActiveState();
  }

  handleIntersect(entries) {
    this.isVisible = entries[0]?.isIntersecting ?? true;
  }

  handleMove(event) {
    if (!this.isVisible) return;
    if (this.mobileQuery.matches || this.motionQuery.matches) return;
    if (event.pointerType === 'touch') return;

    const width = window.innerWidth;
    if (width === 0) return;

    const normalizedX = event.clientX / width;
    // Cursor at viewport left  (normalizedX = 0) -> bias 1 (left-state)
    // Cursor at viewport right (normalizedX = 1) -> bias 0 (right-state)
    const bias = Math.min(Math.max(1 - normalizedX, 0), 1);

    this.scheduleBiasUpdate(bias);
  }

  scheduleBiasUpdate(bias) {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.setBias(bias);
    });
  }

  setBias(bias) {
    if (bias === this.lastBias) return;
    this.lastBias = bias;
    this.style.setProperty('--ag-bias', bias.toFixed(4));
  }

  handlePlay(event) {
    if (event) event.preventDefault();
    this.play();
  }

  handleStop(event) {
    if (event) event.preventDefault();
    this.stop();
  }

  handleEmbedClick(event) {
    if (event.target === this.embed) this.stop();
  }

  handleKeyDown(event) {
    if (!this.classList.contains('is-playing')) return;

    if (event.key === 'Escape') {
      this.stop();
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    // Focus trap — only the close button and the iframe element itself are
    // focusable inside the modal. (Tabbing _into_ the iframe hands focus over
    // to YouTube/Vimeo's own keyboard handling; tabbing back out lands here
    // again so the loop stays closed.)
    const focusable = [this.closeBtn, this.iframe].filter(Boolean);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    // If focus has somehow escaped the modal, pull it back to the close button.
    if (!this.embed.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  buildEmbedSrc() {
    if (!this.videoId || !this.videoType) return '';
    if (this.videoType === 'youtube') {
      // enablejsapi/origin: lets assets/datalayer-tracking.js attach a
      // YT.Player for video_played / video_completed dataLayer events.
      return `https://www.youtube.com/embed/${this.videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
    }
    if (this.videoType === 'vimeo') {
      return `https://player.vimeo.com/video/${this.videoId}?autoplay=1`;
    }
    return '';
  }

  play() {
    const src = this.buildEmbedSrc();
    if (!src || !this.embed || !this.iframe) return;

    this.iframe.src = src;
    this.embed.classList.add('is-open');
    this.embed.setAttribute('aria-hidden', 'false');
    this.classList.add('is-playing');
    window.ScrollLock ? ScrollLock.lock('athlete-gallery') : (document.body.style.overflow = 'hidden');

    if (this.closeBtn) this.closeBtn.focus();
  }

  stop() {
    if (!this.embed || !this.iframe) return;
    if (!this.embed.classList.contains('is-open')) return;

    this.embed.classList.remove('is-open');
    this.embed.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-playing');
    window.ScrollLock ? ScrollLock.unlock('athlete-gallery') : (document.body.style.overflow = '');

    this.embed.addEventListener(
      'transitionend',
      () => {
        if (!this.embed.classList.contains('is-open')) {
          this.iframe.src = '';
        }
      },
      { once: true }
    );

    if (this.videoTrigger) this.videoTrigger.focus();
  }
}

if (!customElements.get('athlete-gallery')) {
  customElements.define('athlete-gallery', AthleteGallery);
}
